import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, Search, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { adminApi, type AdminOrganization, type AdminPlan } from '../../services/api'

export default function AdminOrganizationsPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [page, setPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingOrg, setEditingOrg] = useState<AdminOrganization | null>(null)

  const isActiveParam = statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useQuery({
    queryKey: ['admin-organizations', { search, isActive: isActiveParam, page }],
    queryFn: () => adminApi.listOrganizations({ search: search || undefined, isActive: isActiveParam, page, pageSize: 20 }).then(r => r.data),
  })

  const { data: stats } = useQuery({
    queryKey: ['admin-organization-stats'],
    queryFn: () => adminApi.getOrganizationStats().then(r => r.data),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.updateOrganization(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
      queryClient.invalidateQueries({ queryKey: ['admin-organization-stats'] })
    },
  })

  const statuses = [
    { value: undefined, label: 'Wszystkie' },
    { value: 'active', label: 'Aktywne' },
    { value: 'inactive', label: 'Nieaktywne' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizacje</h1>
          <p className="text-gray-500">Zarządzanie organizacjami w systemie</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nowa organizacja
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-sm text-gray-500">Wszystkie</p>
            <p className="text-2xl font-semibold">{stats.totalOrganizations}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Aktywne</p>
            <p className="text-2xl font-semibold text-green-600">{stats.activeOrganizations}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Trial</p>
            <p className="text-2xl font-semibold text-blue-600">{stats.trialOrganizations}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Nowe w tym miesiącu</p>
            <p className="text-2xl font-semibold text-purple-600">{stats.newOrganizationsThisMonth}</p>
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Szukaj po nazwie lub NIP..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          {statuses.map((s) => (
            <button
              key={s.value ?? 'all'}
              onClick={() => { setStatusFilter(s.value); setPage(1) }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : data?.items?.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nazwa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Użytkownicy</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dokumenty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utworzono</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akcje</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((org) => (
                    <tr key={org.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{org.name}</p>
                          {org.nip && <p className="text-xs text-gray-500">NIP: {org.nip}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{org.planName}</td>
                      <td className="px-4 py-3 text-sm">{org.usersCount}</td>
                      <td className="px-4 py-3 text-sm">{org.documentsCount}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          org.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {org.isActive ? 'Aktywna' : 'Nieaktywna'}
                        </span>
                        {org.status === 'trial' && (
                          <span className="ml-1 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                            Trial
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(org.createdAt).toLocaleDateString('pl-PL')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingOrg(org)}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={() => toggleActiveMutation.mutate({ id: org.id, isActive: !org.isActive })}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              org.isActive
                                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {org.isActive ? 'Dezaktywuj' : 'Aktywuj'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-gray-500">
                  Strona {data.page} z {data.totalPages} ({data.totalCount} wyników)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                    disabled={page >= data.totalPages}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p>Brak organizacji</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
            queryClient.invalidateQueries({ queryKey: ['admin-organization-stats'] })
          }}
        />
      )}

      {/* Edit Modal */}
      {editingOrg && (
        <EditOrganizationModal
          organization={editingOrg}
          onClose={() => setEditingOrg(null)}
          onSuccess={() => {
            setEditingOrg(null)
            queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
            queryClient.invalidateQueries({ queryKey: ['admin-organization-stats'] })
          }}
        />
      )}
    </div>
  )
}

function CreateOrganizationModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [name, setName] = useState('')
  const [nip, setNip] = useState('')
  const [address, setAddress] = useState('')
  const [planId, setPlanId] = useState('')
  const [firstUserEmail, setFirstUserEmail] = useState('')
  const [firstUserName, setFirstUserName] = useState('')
  const [error, setError] = useState('')

  const { data: plans } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => adminApi.getPlans().then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: () => adminApi.createOrganization({
      name,
      planId,
      nip: nip || undefined,
      address: address || undefined,
      firstUserEmail: firstUserEmail || undefined,
      firstUserName: firstUserName || undefined,
    }),
    onSuccess: () => onSuccess(),
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Nie udało się utworzyć organizacji')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Nowa organizacja</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa organizacji <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Kancelaria XYZ"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan <span className="text-red-500">*</span>
            </label>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wybierz plan...</option>
              {plans?.map((plan: AdminPlan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.displayName} ({plan.priceMonthly} PLN/mies.)
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">NIP</label>
              <input
                type="text"
                value={nip}
                onChange={(e) => setNip(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1234567890"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ul. Przykładowa 1"
              />
            </div>
          </div>

          <hr />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email pierwszego użytkownika
            </label>
            <input
              type="email"
              value={firstUserEmail}
              onChange={(e) => setFirstUserEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="admin@kancelaria.pl"
            />
            <p className="text-xs text-gray-400 mt-1">Opcjonalne. Użytkownik otrzyma zaproszenie.</p>
          </div>

          {firstUserEmail && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nazwa użytkownika
              </label>
              <input
                type="text"
                value={firstUserName}
                onChange={(e) => setFirstUserName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Jan Kowalski"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="btn-secondary px-4 py-2 text-sm">
            Anuluj
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!name || !planId || createMutation.isPending}
            className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
          >
            {createMutation.isPending ? 'Tworzenie...' : 'Utwórz'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EditOrganizationModal({
  organization,
  onClose,
  onSuccess,
}: {
  organization: AdminOrganization
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState(organization.name)
  const [nip, setNip] = useState(organization.nip || '')
  const [address, setAddress] = useState(organization.address || '')
  const [planId, setPlanId] = useState(organization.planId || '')
  const [error, setError] = useState('')

  const { data: plans } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => adminApi.getPlans().then(r => r.data),
  })

  const updateMutation = useMutation({
    mutationFn: () => adminApi.updateOrganization(organization.id, {
      name: name !== organization.name ? name : undefined,
      nip,
      address,
      planId: planId !== organization.planId ? planId : undefined,
    }),
    onSuccess: () => onSuccess(),
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Nie udało się zaktualizować organizacji')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Edytuj organizację</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa organizacji
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wybierz plan...</option>
              {plans?.map((plan: AdminPlan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.displayName} ({plan.priceMonthly} PLN/mies.)
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">NIP</label>
              <input
                type="text"
                value={nip}
                onChange={(e) => setNip(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="card p-3 bg-gray-50">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500">Użytkownicy</p>
                <p className="text-lg font-semibold">{organization.usersCount}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Projekty</p>
                <p className="text-lg font-semibold">{organization.projectsCount}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Dokumenty</p>
                <p className="text-lg font-semibold">{organization.documentsCount}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="btn-secondary px-4 py-2 text-sm">
            Anuluj
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={!name || updateMutation.isPending}
            className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Zapisywanie...' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  )
}
